/**
 * Ordner-Upload mit automatischer Dokument-Triage.
 *
 * Nutzer laden oft komplette Makler-Ordner hoch (verschachtelt, mit
 * Foto-Unterordnern, Duplikaten und System-Junk). Dieses Modul sammelt
 * rekursiv alle Dateien ein und priorisiert sie nach Analyse-Relevanz,
 * sodass das Datei-Limit von den wichtigen Unterlagen (Teilungserklärung,
 * Wirtschaftsplan, Protokolle, …) belegt wird — nicht von 17 Wohnungsfotos.
 *
 * Die Heuristik arbeitet nur mit Dateiname/Pfad/Größe (kein Inhalt): Sie
 * entscheidet, WAS hochgeladen wird. Die inhaltliche Erkennung übernimmt
 * danach der Scan-Schritt der Analyse serverseitig.
 */

export type TriagedFile = {
  file: File;
  /** Pfad relativ zum gewählten Ordner, z. B. "Protokolle/ETV 2024.pdf". */
  relPath: string;
};

export type SkippedFile = {
  name: string;
  reason: string;
};

export type TriageResult = {
  selected: File[];
  skipped: SkippedFile[];
};

/** Muss mit MAX_PDF_FILES in AnalysePage übereinstimmen (Server erlaubt 20). */
export const TRIAGE_MAX_FILES = 15;

/**
 * So viele Objektfotos (Wohnungs-/Gebäudebilder) werden mitgenommen, sofern
 * nach den Dokumenten noch Platz ist: Der Server extrahiert daraus das
 * Titelbild der Immobilie, und die Vision-Analyse kann den Zustand bewerten.
 * Fotos verdrängen nie Dokumente — sie füllen nur freie Slots.
 */
export const MAX_OBJECT_PHOTOS = 2;

const JUNK_NAME = /^(\.|~\$)|^(thumbs\.db|desktop\.ini|\.ds_store)$/i;
const JUNK_PATH_SEGMENT = /^(__macosx|\.git)$/i;
/** Ordnernamen, deren Bilder typischerweise Objektfotos sind (keine Dokumente). */
const PHOTO_DIR = /foto|bilder|photo|image|impression/i;
/** Bild-Dateinamen, die auf ein Dokument statt ein Wohnungsfoto hindeuten. */
const DOC_IMAGE_NAME = /grundriss|lageplan|plan|skizze|ausweis|energie|expos|abrechnung|protokoll|grundbuch/i;
/** Generische Kamera-/Export-Dateinamen. */
const CAMERA_NAME = /^(img|dsc|dscn|pxl|screenshot|photo|whatsapp[- ]?(image|bild))[-_ ]?\d/i;

function isPdf(f: File): boolean {
  const t = (f.type || '').toLowerCase();
  return t === 'application/pdf' || t === 'application/x-pdf' || /\.pdf$/i.test(f.name.trim());
}

function isImage(f: File): boolean {
  const t = (f.type || '').toLowerCase();
  return t.startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp|tiff?)$/i.test(f.name.trim());
}

/**
 * Relevanz-Score nach Dateiname (höher = wichtiger). Orientiert sich an der
 * Dokumenten-Checkliste der Analyse (DOKUMENT_CHECK_ART_ORDER).
 */
export function scoreDocumentName(nameOrPath: string): number {
  const s = nameOrPath.toLowerCase();
  if (/teilungserkl|gemeinschaftsordnung|\bte[-_ ]/.test(s)) return 100;
  if (/wirtschaftsplan/.test(s)) return 95;
  if (/jahresabrechnung|hausgeldabrechnung|nebenkostenabrechnung|weg[-_ ]?abrechnung|abrechnung/.test(s)) return 90;
  if (/protokoll|eigent(ü|ue)merversammlung|\betv\b/.test(s)) return 88;
  if (/energieausweis|energiepass|verbrauchsausweis|bedarfsausweis/.test(s)) return 86;
  if (/grundbuch/.test(s)) return 84;
  if (/expos(é|e)|verkaufsunterlagen/.test(s)) return 80;
  if (/mietvertrag|mietaufstellung|mieterliste|rent[-_ ]?roll/.test(s)) return 78;
  if (/wohnfl(ä|ae)che|aufteilungsplan|grundriss|lageplan/.test(s)) return 72;
  if (/sanierung|modernisierung|isfp|sanierungsfahrplan/.test(s)) return 70;
  if (/versicherung|geb(ä|ae)udeversicherung/.test(s)) return 55;
  if (/hausordnung/.test(s)) return 45;
  if (/kaufabsicht|reservierung|finanzierungsbest/.test(s)) return 30;
  // Unbekannte PDFs mittig einsortieren: lieber mitnehmen als verlieren.
  return 60;
}

function hasJunkPath(relPath: string): boolean {
  return relPath.split('/').some((seg) => JUNK_PATH_SEGMENT.test(seg));
}

function isInPhotoDir(relPath: string): boolean {
  const dirs = relPath.split('/').slice(0, -1);
  return dirs.some((seg) => PHOTO_DIR.test(seg));
}

/**
 * Wählt aus einem (ggf. verschachtelten) Datei-Set die relevantesten
 * Unterlagen aus und begründet jede übersprungene Datei.
 */
export function triageDocuments(
  all: TriagedFile[],
  opts?: { maxFiles?: number; maxBytes?: number }
): TriageResult {
  const maxFiles = opts?.maxFiles ?? TRIAGE_MAX_FILES;
  const maxBytes = opts?.maxBytes ?? 50 * 1024 * 1024;

  const skipped: SkippedFile[] = [];
  const seen = new Set<string>();
  const candidates: { tf: TriagedFile; score: number }[] = [];
  const photoCandidates: TriagedFile[] = [];

  for (const tf of all) {
    const { file, relPath } = tf;
    const base = file.name.trim();

    if (JUNK_NAME.test(base) || hasJunkPath(relPath)) {
      // System-Dateien kommentarlos verwerfen — die will niemand im Hinweis lesen.
      continue;
    }
    if (file.size === 0) {
      skipped.push({ name: relPath, reason: 'leere Datei' });
      continue;
    }
    const pdf = isPdf(file);
    const img = !pdf && isImage(file);
    if (!pdf && !img) {
      skipped.push({ name: relPath, reason: 'kein PDF/Bild' });
      continue;
    }
    if (file.size > maxBytes) {
      skipped.push({ name: relPath, reason: `zu groß (${(file.size / 1024 / 1024).toFixed(0)} MB)` });
      continue;
    }
    const dedupeKey = `${base.toLowerCase()}|${file.size}`;
    if (seen.has(dedupeKey)) {
      skipped.push({ name: relPath, reason: 'Duplikat' });
      continue;
    }
    seen.add(dedupeKey);
    if (img && (isInPhotoDir(relPath) || CAMERA_NAME.test(base)) && !DOC_IMAGE_NAME.test(relPath)) {
      // Objektfotos konkurrieren nicht mit Dokumenten — sie füllen nur freie
      // Slots (Titelbild + visueller Zustands-Eindruck), Rest wird verworfen.
      photoCandidates.push(tf);
      continue;
    }
    // Bilder generell hinter PDFs einsortieren: gescannte Dokumente als Bild
    // sind ok, aber ein PDF gleichen Themas ist fast immer die bessere Quelle.
    const score = scoreDocumentName(relPath) - (img ? 25 : 0);
    candidates.push({ tf, score });
  }

  const ordered = candidates
    .map((c, i) => ({ ...c, i }))
    .sort((a, b) => b.score - a.score || a.i - b.i);

  const selected = ordered.slice(0, maxFiles).map((c) => c.tf.file);
  for (const c of ordered.slice(maxFiles)) {
    skipped.push({ name: c.tf.relPath, reason: `Datei-Limit (max. ${maxFiles}) — niedriger priorisiert` });
  }

  // Die größten Fotos zuerst (meist Außen-/Hauptansichten in voller Auflösung).
  const photoRoom = Math.min(MAX_OBJECT_PHOTOS, Math.max(0, maxFiles - selected.length));
  const photosBySize = [...photoCandidates].sort((a, b) => b.file.size - a.file.size);
  selected.push(...photosBySize.slice(0, photoRoom).map((p) => p.file));
  for (const p of photosBySize.slice(photoRoom)) {
    skipped.push({
      name: p.relPath,
      reason:
        photoRoom > 0
          ? `Objektfoto — nur ${photoRoom === 1 ? 'das größte wird' : `die ${photoRoom} größten werden`} fürs Titelbild mitgeschickt`
          : 'Objektfoto — kein Platz mehr im Datei-Limit',
    });
  }
  return { selected, skipped };
}

/* ── Einsammeln aus Drag&Drop bzw. Ordner-Picker ─────────────────── */

type FsEntry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?: (cb: (f: File) => void, err?: (e: unknown) => void) => void;
  createReader?: () => {
    readEntries: (cb: (entries: FsEntry[]) => void, err?: (e: unknown) => void) => void;
  };
};

function readAllEntries(dir: FsEntry): Promise<FsEntry[]> {
  const reader = dir.createReader!();
  return new Promise((resolve) => {
    const out: FsEntry[] = [];
    // readEntries liefert max. ~100 Einträge pro Aufruf → wiederholen bis leer.
    const step = () =>
      reader.readEntries(
        (batch) => {
          if (!batch.length) return resolve(out);
          out.push(...batch);
          step();
        },
        () => resolve(out)
      );
    step();
  });
}

function entryToFile(entry: FsEntry): Promise<File | null> {
  return new Promise((resolve) => {
    if (!entry.file) return resolve(null);
    entry.file(
      (f) => resolve(f),
      () => resolve(null)
    );
  });
}

async function walkEntry(entry: FsEntry, prefix: string, out: TriagedFile[]): Promise<void> {
  if (entry.isFile) {
    const f = await entryToFile(entry);
    if (f) {
      const relPath = `${prefix}${entry.name}`;
      try {
        // Pfad an der File annotieren (wie beim webkitdirectory-Picker), damit
        // die UI zeigen kann, aus welchem Unterordner eine Datei stammt.
        Object.defineProperty(f, 'webkitRelativePath', { value: relPath });
      } catch {
        /* read-only in exotischen Browsern — Pfadanzeige entfällt dann */
      }
      out.push({ file: f, relPath });
    }
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllEntries(entry);
    for (const child of children) {
      await walkEntry(child, `${prefix}${entry.name}/`, out);
    }
  }
}

/** Enthält der Drop mindestens einen Ordner? (Nur synchron im Drop-Handler aufrufbar.) */
export function dropContainsDirectory(dt: DataTransfer): boolean {
  const items = dt.items;
  if (!items) return false;
  for (let i = 0; i < items.length; i++) {
    const getEntry = (items[i] as DataTransferItem & { webkitGetAsEntry?: () => FsEntry | null })
      .webkitGetAsEntry;
    const entry = typeof getEntry === 'function' ? getEntry.call(items[i]) : null;
    if (entry?.isDirectory) return true;
  }
  return false;
}

/**
 * Sammelt alle Dateien eines Drops rekursiv ein (Ordner werden durchlaufen).
 * WICHTIG: synchron im Drop-Handler aufrufen — `webkitGetAsEntry()` liefert
 * nach dem ersten `await` nichts mehr.
 */
export function collectFilesFromDrop(dt: DataTransfer): Promise<TriagedFile[]> {
  const entries: FsEntry[] = [];
  const plainFiles: File[] = [];
  const items = dt.items;
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i] as DataTransferItem & { webkitGetAsEntry?: () => FsEntry | null };
      const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
      if (entry) {
        entries.push(entry);
      } else if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) plainFiles.push(f);
      }
    }
  }
  if (!entries.length && !plainFiles.length) {
    // Fallback für Browser ohne webkitGetAsEntry
    return Promise.resolve(Array.from(dt.files || []).map((f) => ({ file: f, relPath: f.name })));
  }
  return (async () => {
    const out: TriagedFile[] = plainFiles.map((f) => ({ file: f, relPath: f.name }));
    for (const entry of entries) {
      await walkEntry(entry, '', out);
    }
    return out;
  })();
}

/** Dateien aus einem `<input webkitdirectory>` inkl. relativer Pfade. */
export function collectFilesFromDirectoryInput(list: FileList): TriagedFile[] {
  return Array.from(list).map((f) => ({
    file: f,
    relPath: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
  }));
}

/**
 * Anzeigename einer Datei inkl. Unterordner (ohne den Wurzelordner selbst),
 * z. B. "Protokolle/ETV 2024.pdf". Für Einzeldateien nur der Dateiname.
 */
export function fileDisplayLabel(f: File): string {
  const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (!rel || rel === f.name) return f.name;
  const parts = rel.split('/');
  return parts.length > 2 ? parts.slice(1).join('/') : f.name;
}

/** Kompakte, menschenlesbare Zusammenfassung der übersprungenen Dateien. */
export function summarizeSkipped(skipped: SkippedFile[]): string | null {
  if (!skipped.length) return null;
  const byReason = new Map<string, number>();
  for (const s of skipped) byReason.set(s.reason, (byReason.get(s.reason) ?? 0) + 1);
  const parts = [...byReason.entries()].map(([reason, n]) =>
    n === 1 ? `1 Datei (${reason})` : `${n} Dateien (${reason})`
  );
  return `Automatisch aussortiert: ${parts.join(', ')}.`;
}
